import { useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { PermissionCheck, ResourceAction } from '@/types'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'

// Query keys
export const permissionKeys = {
  all: ['permissions'] as const,
  user: () => [...permissionKeys.all, 'user'] as const,
}

/**
 * Hook to fetch and check user permissions
 */
export function usePermissions() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const forcePasswordChange = useAuthStore((state) => state.forcePasswordChange)

  const {
    data: permissionsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: permissionKeys.user(),
    queryFn: async (): Promise<string[]> => {
      const response = await api.get<string[]>('/api/me/permissions')
      return response.data ?? []
    },
    enabled: isAuthenticated && !forcePasswordChange,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
  })

  // Memoize the fallback so the array ref is stable across renders (avoids
  // recomputing permissionSet below on every render when permissionsData is undefined).
  const permissions = useMemo(() => permissionsData ?? [], [permissionsData])

  // Create a permission set for O(1) lookups
  // API returns strings like "users.read", "roles.create"
  const permissionSet = useMemo(() => {
    const set = new Set<string>()
    permissions.forEach((permission) => {
      // Add the original permission string (e.g., "users.read")
      set.add(permission)
      // Also add with colon format for compatibility (e.g., "users:read")
      const colonFormat = permission.replace('.', ':')
      set.add(colonFormat)
    })
    return set
  }, [permissions])

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback(
    (resource: string, action: ResourceAction): boolean => {
      if (!isAuthenticated || forcePasswordChange) {
        return false
      }

      const dotFormat = `${resource}.${action}`
      const colonFormat = `${resource}:${action}`
      if (permissionSet.has(dotFormat) || permissionSet.has(colonFormat)) {
        return true
      }

      const manageDot = `${resource}.manage`
      const manageColon = `${resource}:manage`
      if (permissionSet.has(manageDot) || permissionSet.has(manageColon)) {
        return true
      }

      if (permissionSet.has(`*.${action}`) || permissionSet.has(`*:${action}`)) {
        return true
      }

      if (permissionSet.has('*.manage') || permissionSet.has('*:manage') ||
          permissionSet.has('*.*') || permissionSet.has('*:*')) {
        return true
      }

      return false
    },
    [isAuthenticated, forcePasswordChange, permissionSet]
  )

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = useCallback(
    (checks: PermissionCheck[]): boolean => {
      return checks.some((check) => hasPermission(check.resource, check.action))
    },
    [hasPermission]
  )

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = useCallback(
    (checks: PermissionCheck[]): boolean => {
      return checks.every((check) => hasPermission(check.resource, check.action))
    },
    [hasPermission]
  )

  /**
   * Check if user can perform an action
   * @param permission - Permission string in format "resource:action" or "resource.action"
   */
  const can = useCallback(
    (permission: string): boolean => {
      if (permission.includes(':')) {
        const [resource, action] = permission.split(':') as [string, ResourceAction]
        return hasPermission(resource, action)
      }

      if (permission.includes('.')) {
        const [resource, action] = permission.split('.') as [string, ResourceAction]
        return hasPermission(resource, action)
      }

      return permissionSet.has(permission)
    },
    [hasPermission, permissionSet]
  )

  return {
    permissions,
    isLoading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    can,
    permissionSet,
  }
}
